import React, { useMemo, useState } from "react";

import {
  Minus,
  Plus,
  Trash2,
  Wallet,
  Percent,
  Ban,
  CreditCard,
  Package2,
} from "lucide-react";

import "../css/controlPanel.css";

const ControlPanel = ({
  selectedProduct,
  cart,
  setCart,
  processPayment,
  openDiscountModal,
  onIncQty,
  onDecQty,
  onSetQty,
}) => {

  const norm = (n) =>
    Number(n ?? 0).toFixed(2);

  const sameLine = (a, b) =>
    a?.id === b?.id &&
    norm(a?.price) === norm(b?.price) &&
    (a?.discountType || "") ===
      (b?.discountType || "") &&
    norm(a?.discount) ===
      norm(b?.discount) &&
    (a?.metaSource || "") ===
      (b?.metaSource || "");

  const lineIndex = useMemo(() => {

    if (!selectedProduct) return -1;

    const byRef = cart.findIndex(
      (i) => i === selectedProduct
    );

    if (byRef !== -1) return byRef;

    return cart.findIndex((i) =>
      sameLine(i, selectedProduct)
    );

  }, [cart, selectedProduct]);

  const line =
    lineIndex >= 0
      ? cart[lineIndex]
      : null;

  const usesStock =
    !!line?.uses_stock;

  const [qtyInput, setQtyInput] =
    useState("");

  const commitQty = () => {

    if (lineIndex < 0) return;

    const val = Number(
      qtyInput ||
      line?.quantity ||
      1
    );

    onSetQty?.(lineIndex, val);
  };

  const deleteProduct = () => {

    if (lineIndex < 0) return;

    setCart(
      cart.filter(
        (_, idx) => idx !== lineIndex
      )
    );
  };

  const applyDiscount = () => {

    if (lineIndex < 0) return;

    const discount = prompt(
      "Enter discount percentage:",
      "0"
    );

    if (discount === null) return;

    const pct = Number(discount);

    if (Number.isNaN(pct)) return;

    const item = cart[lineIndex];

    const discountedPrice =
      Number(item.price) *
      (1 - pct / 100);

    setCart(
      cart.map((it, idx) =>
        idx === lineIndex
          ? {
              ...it,
              price:
                +discountedPrice.toFixed(2),
            }
          : it
      )
    );
  };

  const voidOrder = () =>
    setCart([]);

  const openCashDrawer = () =>
    alert("Opening Cash Drawer...");

  return (
    <div className="modern-control-panel">

      {/* =========================
          QUANTITY
      ========================== */}
      <div className="modern-cp-card">

        <div className="modern-cp-header">

          <div className="modern-cp-header-left">

            <div className="modern-cp-icon">
              <Package2 size={16} />
            </div>

            <div>
              <h4>Quantity</h4>

              <span>
                Adjust selected item
              </span>
            </div>

          </div>

        </div>

        <div className="modern-cp-qty-row">

          <button
            className="modern-cp-qty-btn"
            disabled={
              !usesStock ||
              lineIndex < 0
            }
            onClick={() =>
              onDecQty?.(lineIndex)
            }
          >
            <Minus size={16} />
          </button>

          <input
            className="modern-cp-input"
            type="number"
            min={1}
            disabled={
              !usesStock ||
              lineIndex < 0
            }
            value={
              qtyInput !== ""
                ? qtyInput
                : line?.quantity ?? 1
            }
            onChange={(e) =>
              setQtyInput(
                e.target.value
              )
            }
            onBlur={commitQty}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              commitQty()
            }
          />

          <button
            className="modern-cp-qty-btn"
            disabled={
              !usesStock ||
              lineIndex < 0
            }
            onClick={() =>
              onIncQty?.(lineIndex)
            }
          >
            <Plus size={16} />
          </button>

        </div>

        <div className="modern-cp-hint">

          {line ? (
            usesStock ? (
              <>
                In stock:
                {" "}
                {line.product_quantity ??
                  line.available_quantity ??
                  0}
              </>
            ) : (
              <>
                Selected item
                doesn’t use stock
              </>
            )
          ) : (
            <>
              Select a line to
              adjust quantity
            </>
          )}

        </div>

      </div>

      {/* =========================
          ACTIONS
      ========================== */}
      <div className="modern-cp-actions-grid">

        <button
          onClick={deleteProduct}
          className="modern-cp-action delete"
        >
          <Trash2 size={16} />
          <span>Delete</span>
        </button>

        <button
          onClick={openCashDrawer}
          className="modern-cp-action drawer"
        >
          <Wallet size={16} />
          <span>Cash Drawer</span>
        </button>

        <button
          onClick={
            openDiscountModal ??
            applyDiscount
          }
          className="modern-cp-action discount"
        >
          <Percent size={16} />
          <span>Discount</span>
        </button>

        <button
          onClick={voidOrder}
          className="modern-cp-action void"
        >
          <Ban size={16} />
          <span>Void Order</span>
        </button>

      </div>

      {/* =========================
          PAYMENT
      ========================== */}
      <button
        className="modern-cp-payment-btn"
        onClick={processPayment}
      >
        <CreditCard size={18} />

        <span>
          Payment (F10 / +)
        </span>

      </button>

    </div>
  );
};
export default ControlPanel;